import { ROOM_COLOR_SELECTOR_PALETTE } from "@/utils/colors";
import { Color } from "three";
import { v4 as uuidv4 } from "uuid";
import { WALL_PROP_COLORS, WallConfig } from "./WallConfig";
import { WINDOW_DEFAULT_FLOOR_OFFSET } from "./createWallProp";

export const MIN_ROOM_HEIGHT = 2.5;
export const MAX_ROOM_HEIGHT = 10;
export const MIN_ROOM_SIZE = 4;
export const MAX_ROOM_SIZE = 20;

const DEFAULT_ROOM_HEIGHT = 3.2;

export const DEFAULT_WINDOW_PROPS = {
    height: DEFAULT_ROOM_HEIGHT - WINDOW_DEFAULT_FLOOR_OFFSET - 0.7,
    floorOffset: WINDOW_DEFAULT_FLOOR_OFFSET,
    width: 2,
};

export const DEFAULT_DOOR_PROPS = {
    height: 2,
    width: 1,
};

type Walls = {
    leftWall: WallConfig;
    rightWall: WallConfig;
    frontWall: WallConfig;
    backWall: WallConfig;
};

export type RoomConfig = {
    width: number;
    depth: number;
    height: number;
    floorColor: Color;
} & Walls;

export type WallKey = keyof Walls;

export const isWallKey = (key: string): key is WallKey => {
    return key === "leftWall" || key === "rightWall" || key === "frontWall" || key === "backWall";
};

export const getWallSizePropForWallKey = (wallKey: WallKey): "width" | "depth" => {
    return wallKey === "leftWall" || wallKey === "rightWall" ? "depth" : "width";
};

export const getDefaultRoomConfig = (): RoomConfig => {
    const wallThickness = 0.16;
    const wallColor = ROOM_COLOR_SELECTOR_PALETTE[1];
    const floorColor = ROOM_COLOR_SELECTOR_PALETTE[2];

    return {
        width: 8,
        depth: 6,
        height: DEFAULT_ROOM_HEIGHT,
        floorColor,
        leftWall: {
            key: "leftWall",
            thickness: wallThickness,
            color: wallColor,
            baseboardColor: wallColor,
            props: [
                {
                    type: "door",
                    width: DEFAULT_DOOR_PROPS.width,
                    height: DEFAULT_DOOR_PROPS.height,
                    offset: 1,
                    key: uuidv4(),
                    highlightColor: WALL_PROP_COLORS[0],
                },
            ],
        },
        rightWall: {
            key: "rightWall",
            thickness: wallThickness,
            color: wallColor,
            baseboardColor: wallColor,
            props: [
                {
                    type: "window",
                    width: 2.4,
                    height: DEFAULT_WINDOW_PROPS.height,
                    offset: 3,
                    floorOffset: DEFAULT_WINDOW_PROPS.floorOffset,
                    key: uuidv4(),
                    highlightColor: WALL_PROP_COLORS[0],
                },
            ],
        },
        frontWall: {
            key: "frontWall",
            thickness: wallThickness,
            color: wallColor,
            baseboardColor: wallColor,
            props: [
                {
                    type: "window",
                    width: 1.8,
                    height: DEFAULT_WINDOW_PROPS.height,
                    offset: 4,
                    floorOffset: DEFAULT_WINDOW_PROPS.floorOffset,
                    key: uuidv4(),
                    highlightColor: WALL_PROP_COLORS[0],
                },
            ],
        },
        backWall: {
            key: "backWall",
            thickness: wallThickness,
            color: wallColor,
            baseboardColor: wallColor,
            props: [
                {
                    type: "window",
                    width: 1.2,
                    height: DEFAULT_WINDOW_PROPS.height,
                    offset: 3,
                    floorOffset: DEFAULT_WINDOW_PROPS.floorOffset,
                    key: uuidv4(),
                    highlightColor: WALL_PROP_COLORS[0],
                },
            ],
        },
    };
};

type Position = [x: number, y: number, z: number];

export const getWallPosition = (
    wall: WallConfig,
    room: RoomConfig,
    options?: { includeThickness?: boolean; includeHeight?: boolean }
): Position => {
    const { includeThickness = false, includeHeight = false } = options ?? {};

    if (wall.key === room.leftWall.key) {
        return [
            -room.width / 2 - (includeThickness ? wall.thickness / 2 : 0),
            includeHeight ? room.height / 2 : 0,
            0,
        ];
    }

    if (wall.key === room.rightWall.key) {
        return [
            room.width / 2 + (includeThickness ? wall.thickness / 2 : 0),
            includeHeight ? room.height / 2 : 0,
            0,
        ];
    }

    if (wall.key === room.frontWall.key) {
        return [
            0,
            includeHeight ? room.height / 2 : 0,
            room.depth / 2 + (includeThickness ? wall.thickness / 2 : 0),
        ];
    }

    if (wall.key === room.backWall.key) {
        return [
            0,
            includeHeight ? room.height / 2 : 0,
            -room.depth / 2 - (includeThickness ? wall.thickness / 2 : 0),
        ];
    }

    return [0, 0, 0];
};

export const getWallRotation = (wall: WallConfig, room: RoomConfig): number => {
    if (wall.key === room.leftWall.key) {
        return Math.PI / 2;
    }

    if (wall.key === room.rightWall.key) {
        return -Math.PI / 2;
    }

    if (wall.key === room.backWall.key) {
        return 0;
    }

    if (wall.key === room.frontWall.key) {
        return Math.PI;
    }

    return 0;
};
